import { json, LinksFunction, LoaderFunction } from '@remix-run/node';

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigation,
  useFetchers,
  useLocation,
  useLoaderData,
} from '@remix-run/react';
import NProgress from 'nprogress';
import tailwindCss from '~/styles/tailwind.css?url'
import nProgressCss from '~/styles/nprogress.css?url';
import globalCss from '~/styles/global.css?url';
import { useEffect } from 'react';
import * as gtag from '~/lib/gtags.client';
import RootErrorBoundary from '~/components/RootErrorBoundary';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: tailwindCss },
  { rel: 'stylesheet', href: nProgressCss },
  { rel: 'stylesheet', href: globalCss },
]

export const Layout = ({ children, title, noindex = false }: { children: React.ReactNode, title?: string, noindex?: boolean }) => {
  return (
    <html lang="ja">
      <head>
        {title ? <title>{title}</title> : null}
        {noindex ? <meta name="robots" content="noindex" /> : null}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon/favicon2.svg" sizes="any" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/favicon/apple-touch-icon.png" />
        <link rel="manifest" href="/favicon/site.webmanifest"/>
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Load the GA tracking id from the .env
export const loader: LoaderFunction = async () => {
  return json({
    ENV: {
      PUBLIC_GA_TRACKING_ID: process.env.PUBLIC_GA_TRACKING_ID || '',
      PUBLIC_SENTRY_DSN: process.env.PUBLIC_SENTRY_DSN || '',
    }
  });
};

const App = () => {
  // nprogressを使ってページ遷移時にローディングバーを表示する
  const navigation = useNavigation();
  const fetchers = useFetchers();
  const fetchersIdle = fetchers.every((f) => f.state === 'idle');
  useEffect(() => {
    if (navigation.state === 'idle' && fetchersIdle) {
      NProgress.done();
    } else {
      NProgress.start();
    }
  }, [navigation, fetchersIdle]);


  const location = useLocation();
  const data = useLoaderData<typeof loader>() || {};
  const { ENV } = data;
  const { PUBLIC_GA_TRACKING_ID } = ENV;

  // GA4を初期化
  useEffect(() => {
    if (PUBLIC_GA_TRACKING_ID?.length) {
      gtag.pageview(location.pathname, PUBLIC_GA_TRACKING_ID);
    }
  }, [location, PUBLIC_GA_TRACKING_ID]);

  return (
    <>
      {process.env.NODE_ENV === 'development' || !PUBLIC_GA_TRACKING_ID ? null : (
        <>
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${PUBLIC_GA_TRACKING_ID}`}
          />
          <script
            async
            id="gtag-init"
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${PUBLIC_GA_TRACKING_ID}', {
                page_path: window.location.pathname,
              });
            `,
            }}
          />
        </>
      )}
      {/*
      publicな環境変数をwindowで保持してブラウザで使えるようにする
        @ref Environment Variables | Remix https://remix.run/docs/en/main/guides/envvars#browser-environment-variables
        @ref remixのクライアントサイドで環境変数を参照する方法 https://sendo-blog.com/article/remix-env-client
      */}
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(
            data.ENV
          )}`,
        }}
      />
      <Outlet />
    </>
  );
}
export default App;

export const ErrorBoundary = RootErrorBoundary;
